import type { Option } from "@tento-lona/sheets";
import { html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { Button } from "@tento-ui/components/button/button";
import { Disclaimer } from "@tento-ui/components/disclaimer";
import { Divider } from "@tento-ui/components/divider";
import { Overlay } from "@tento-ui/components/overlay";
import { RadioButtonCell } from "@tento-ui/components/radio-button-cell";
import { DomUtils } from "@tento-ui/dom";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { LonaModalWebComponent } from "@tento-ui/ui/modal-component";
import { Network } from "@tento-lona/network";
import { LonaModalHeader } from "@tento-ui/ui/modal-header-styles";

const TIME_SCALE_OPTIONS: { value: Network.TimeScaleCustom; label: string }[] =
  [
    { value: "year", label: "Year" },
    { value: "month", label: "Month" },
    { value: "week", label: "Week" },
    { value: "day", label: "Day" },
  ];

@component({
  name: "lona-time-scale-modal",
})
export class TimescaleModal extends LonaModalWebComponent {
  onSave?: (
    timescale: Network.TimeScaleCustom,
  ) => Promise<Option<{ error: string }>>;
  #$optionsContainer = this.$("options-container");
  #$disclaimer: Disclaimer = this.hydrate("disclaimer");
  #$saveButton = this.hydrate("save-button");
  #$cancelButton = this.hydrate("cancel-button");
  #$errorMessage = this.hydrate("error-message");

  #selectedTimescale: Network.TimeScaleCustom = "month";
  #radioGroup = new RadioButtonCell.Group();

  static makeWith(
    currentTimescale: Network.TimeScaleCustom,
    onSave: (
      timescale: Network.TimeScaleCustom,
    ) => Promise<Option<{ error: string }>>,
    showYearTimescale?: boolean,
  ): TimescaleModal {
    const modal = TimescaleModal.make();
    modal.bind(currentTimescale, onSave, showYearTimescale);
    return modal;
  }

  #showYearTimescale = false;

  bind(
    currentTimescale: Network.TimeScaleCustom,
    onSave: (
      timescale: Network.TimeScaleCustom,
    ) => Promise<Option<{ error: string }>>,
    showYearTimescale?: boolean,
  ): this {
    this.#selectedTimescale = currentTimescale;
    this.onSave = onSave;
    this.#showYearTimescale = showYearTimescale ?? false;
    this.#renderTimescaleOptions();
    this.#$disclaimer.bind(
      "Your data will be preserved. You can change this setting back at any time.",
    );
    return this;
  }

  #renderTimescaleOptions() {
    DomUtils.clearChildren(this.#$optionsContainer);

    const $cells: RadioButtonCell[] = [];
    const options = this.#showYearTimescale
      ? TIME_SCALE_OPTIONS
      : TIME_SCALE_OPTIONS.filter((o) => o.value !== "year");
    for (const option of options) {
      const $cell = RadioButtonCell.makeWith({
        title: option.label,
        options: {
          value: option.value,
          name: "timescale",
        },
      });
      $cells.push($cell);
    }

    this.#radioGroup = new RadioButtonCell.Group({
      onSelected: ($cell) => {
        const value = $cell.getAttribute("value") as Network.TimeScaleCustom;
        this.#selectedTimescale = value;
        return true;
      },
    });
    this.#radioGroup.bind($cells);

    // Select the current timescale option
    const currentCell = $cells.find(
      ($c) => $c.getAttribute("value") === this.#selectedTimescale,
    );
    if (currentCell) {
      this.#radioGroup.select(currentCell);
    }

    this.#$optionsContainer.appendChild(this.#radioGroup.build());
  }

  connectedCallback() {
    GESTURE_MANAGER.addPointerEvent(this.#$saveButton, {
      onClick: () => this.#handleSave(),
    });

    GESTURE_MANAGER.addPointerEvent(this.#$cancelButton, {
      onClick: () => this.#handleCancel(),
    });
  }

  disconnectedCallback() {
    GESTURE_MANAGER.unregister(this.#$saveButton);
    GESTURE_MANAGER.unregister(this.#$cancelButton);
  }

  async #handleSave(): Promise<void> {
    this.#clearError();

    const result = await this.onSave?.(this.#selectedTimescale);

    if (result?.error) {
      this.#showError(result.error);
    } else {
      Overlay.dismiss(this);
    }
  }

  #showError(message: string): void {
    this.#$errorMessage.textContent = message;
    this.#$errorMessage.classList.add("visible");
  }

  #clearError(): void {
    this.#$errorMessage.textContent = "";
    this.#$errorMessage.classList.remove("visible");
  }

  #handleCancel(): void {
    Overlay.dismiss(this);
  }

  static $styles: Style.Compat[] = [
    LonaModalWebComponent.$style,
    LonaModalHeader.$style,
    Divider.$hr,
    Button.$style,
    css`
      :host {
        --std-modal-max-width: 400px;
        --std-modal-min-height: auto;
        --std-modal-max-height: calc(100vh - 400px);
        background: var(--bg);
        border-radius: 12px;
      }

      #root {
        display: flex;
        flex-direction: column;
        height: 100%;
      }

      .lona-modal-header {
        padding: 20px 20px;
      }

      hr {
        margin: 0;
      }

      #content {
        padding-block: 12px;
      }

      #options-container {
        padding: 0 12px;
        overflow-y: auto;
      }

      #disclaimer {
        margin: 0 12px 8px;
      }

      #actions {
        position: relative;
        padding: 12px 20px;
        gap: 12px;
        justify-content: flex-end;
        border-top: 1px solid var(--divider);
      }

      #description {
        color: var(--text-sec);
      }

      #title {
        --margin-bottom: 8px;
      }

      #error-message {
        color: var(--red);
        display: none;
        flex-grow: 1;
        align-self: center;
      }

      #error-message.visible {
        display: block;
      }
    `,
  ];

  static $html = html`
    <std-col id="root">
      <std-col class="lona-modal-header">
        <h1 id="title" class="h4">Edit Time Scale</h1>
        <p id="description">
          Change how data is grouped and displayed in this row
        </p>
      </std-col>
      <hr />
      <std-col id="content">
        <std-col id="options-container"></std-col>
      </std-col>
      <std-disclaimer id="disclaimer"></std-disclaimer>
      <std-row id="actions">
        <p id="error-message" class="small"></p>
        <button id="cancel-button">Cancel</button>
        <button id="save-button" primary>Save</button>
      </std-row>
    </std-col>
  `;
}
